import { useEffect, useContext, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { UIContext } from '../../contexts/UIContext';

import { useGetDenounce } from '../../hooks/useDenounceService';
import { useGetDenounceStatusForSelector } from '../../hooks/useDenounceStatusService';
import { useAgentInspectionStatusService } from '../../hooks/useAgentInspectionStatusService';
import { useGetAgentInspection } from '../../hooks/useAgentInspectionService';
import { useGetAddress } from '../../hooks/useAddressService';
import { useGetPopulationUser } from '../../hooks/usePopulationUserService';

import DenounceService from '../../services/DenounceService';
import AddressService from '../../services/AddressService';
import PicturesService from '../../services/PicturesService';
import AgentInspectionService from '../../services/AgentInspectionService';

import AddressForm from '../../components/Forms/AddressForm/AddressForm';
import UpdateDenounceForm from '../../components/Forms/UpdateDenounceForm/UpdateDenounceForm';
import UserCodeForm from '../../components/Forms/UserCodeForm/UserCodeForm';

import { Stack, Pagination, Typography } from '@mui/material';
import Header from '../../components/Header/Header';
import BottomMenu from '../../components/BottomMenu/BottomMenu';
import CameraCapture from '../../components/CameraCapture/CameraCapture';
import InformationDialogue from '../../components/InformationDialogue/InformationDialogue';

import './DenounceUpdate.scss';

function DenounceUpdate() {
  const navigate = useNavigate();
  const location = useLocation();

  const { authUser, isAuthorized } = useContext(AuthContext);
  const {showWarningMessage} = useContext(UIContext);
  const {
    INFO_DIALOGUE_MSGS,
    setOpenInfoDialogue,
    handleCloseInfoDialogue,
    openInfoDialogue,
    infoMsgTitle,
    infoMsgLabel,
    infoMsgContent
  } = useContext(UIContext);
  const [ isLoadingDenounce, denounce] = useGetDenounce({ id: location?.state?.denounceId });
  const [ isLoadingDenounceStatus, denounceStatus] = useGetDenounceStatusForSelector(true);
  const [ , agentInspectionStatus ] = useAgentInspectionStatusService(true);
  const [ isLoadingAgentInspection, agentInspection] = useGetAgentInspection({
    denounceId: denounce?.id
  });
  const [ isLoadingAddress, address] = useGetAddress({
    countryId: agentInspection?.address?.country_id,
    stateId: agentInspection?.address?.state_id,
    cityId: agentInspection?.address?.city_id,
    neighborhood: agentInspection?.address?.neighborhood,
    street: agentInspection?.address?.street,
    number: agentInspection?.address?.number,
    zipCode: agentInspection?.address?.zip_code,
    additionalInfo: agentInspection?.address?.additional_info
  });
  const [ , populationUser] = useGetPopulationUser({
    userId: denounce?.user_id
  });
  const vectorFocusAddress = denounce?.vector_focus?.address;
  const denounceAddress = {
    city: denounce?.city?.name || '',
    neighborhood: address?.neighborhood || vectorFocusAddress?.neighborhood || '',
    street: address?.street || vectorFocusAddress?.street || '',
    number: address?.number || vectorFocusAddress?.number || '',
    complement: address?.additional_info || vectorFocusAddress?.additional_info || '',
    zipCode: address?.zip_code || vectorFocusAddress?.zip_code || ''
  };
  const vectorFocusClosureImageUrl = denounce?.vector_focus?.closure_picture?.url;

  const [page, setPage] = useState(1);
  const [isDoneLoading, setIsDoneLoading] = useState(false);
  const [upsertedAgentInspection, setUpsertedAgentInspection] = useState(null);
  const [vectorFocusImageFile, setVectorFocusImageFile] = useState(null);

  const agentInspectionService = new AgentInspectionService();

  useEffect(() => {
    if (!isAuthorized) navigate('/login');
  }, [isAuthorized, navigate]);

  useEffect(() => {
    let timeoutId = null;

    if (denounce && !isLoadingDenounce && !isLoadingDenounceStatus & !isLoadingAgentInspection & !isLoadingAddress) {
      timeoutId = setTimeout(() => setIsDoneLoading(true), 500);
    }

    return (() => {
      clearTimeout(timeoutId);
    });
  }, [denounce, isLoadingDenounce, isLoadingDenounceStatus, isLoadingAgentInspection, isLoadingAddress])

  const handleChange = (ev, value) => {
    setPage(value);
  };

  async function handleSubmitAddressForm(values, {setSubmitting }) {
    const addressService = new AddressService();
    const repUpsertAddress = await addressService.upsertAddress({
      countryId: denounce?.city?.country_id,
      stateId: denounce?.city?.state_id,
      cityId: denounce?.city?.id,
      neighborhood: values.neighborhood,
      street: values.street,
      number: values.number,
      zipCode: values.zipCode,
      additionalInfo: values.complement
    });
    const address = repUpsertAddress?.data;

    if (repUpsertAddress.status !== 1 || !address) {
      showWarningMessage({
        message: "Não foi possível cadastrar o endereço"
      });
      setSubmitting(false);
      return;
    }

    const repAgentInspection = await agentInspectionService.upsertAgentInspection({
      denounceId: denounce.id,
      addressId: address?.id,
      populationUserId: denounce.user?.id,
      agentUserId: authUser.roles?.agent?.id,
      agentFeedback: denounce.agent_feedback
    });
    const inspection = repAgentInspection?.data;

    if (repAgentInspection.status !== 1 || !inspection) {
      showWarningMessage({
        message: "Não foi possível cadastrar o processo de inspeção"
      });
      setSubmitting(false);
      return;
    }
    setUpsertedAgentInspection(inspection);
    setSubmitting(false);
    setPage(2);
  }

  async function handleSubmitDenounceForm(values, { setSubmitting }) {
    const denounceService = new DenounceService();
    const resUpdateDenounce = await denounceService.updateDenounce({
      denounceId: denounce.id,
      agentUserId: denounce.agent_user_id,
      isAnonymous: denounce.is_anonymous,
      populationUserActualPoints: denounce.population_user_actual_points,
      agentUserActualPoints: denounce.agent_user_actual_points,
      denounceStatusId: values.denounceStatus.id,
      description: values.description,
      agentFeedback: values.agentFeedback,
      agentVisit: values.hasVisited
    });
    const updatedDenounce = resUpdateDenounce?.data;

    if (resUpdateDenounce.status !== 1 || !updatedDenounce) {
      showWarningMessage({
        message: "Não foi possível atualizer a denúncia"
      });
      setSubmitting(false);
      return;
    }

    let picture = null;

    if (vectorFocusImageFile) {
      const pictureService = new PicturesService();
      const resOpPicture = await pictureService.upsertPicture({
        pictureId: denounce?.vector_focus?.closure_picture_id,
        pickedImgFile: vectorFocusImageFile,
        currentUserId: authUser?.user?.id
      });
      picture = resOpPicture?.data;
  
      if (!picture) {
        showWarningMessage({
          message: "Houve uma falha ao enviar a foto atualizada"
        });
        setSubmitting(false);
        return;
      }
    }

    const inspectionStatus = agentInspectionStatus.find(item => item?.name === values.denounceStatus?.name);
    const repAgentInspection = await agentInspectionService.upsertAgentInspection({
      agentInspectionStatusId: inspectionStatus?.id,
      denounceId: updatedDenounce?.id,
      addressId: address?.id,
      populationUserId: updatedDenounce.user?.id,
      agentUserId: authUser.roles?.agent?.id,
      agentFeedback: updatedDenounce.agent_feedback,
      closurePictureId: picture?.id
    });
    const inspection = repAgentInspection?.data;

    if (repAgentInspection.status !== 1 || !inspection) {
      showWarningMessage({
        message: "Houve uma falha ao enviar a foto atualizada"
      });
      setSubmitting(false);
      return;
    }

    setOpenInfoDialogue({
      open: true,
      msgId: INFO_DIALOGUE_MSGS.DENOUNCE_HAS_BEEN_UPDATED,
      callback: () => navigate(-1)
    });
    setUpsertedAgentInspection(inspection);
    setSubmitting(false);
  }
 
  async function handleSubmitUserCode(values, { setSubmitting }) {
    const repAgentInspection = await agentInspectionService.updateAgentInspectionWithCode({
      id: upsertedAgentInspection?.id || agentInspection?.id,
      populationUserId: populationUser?.id,
      agentActualPoints: upsertedAgentInspection?.agent_actual_points || agentInspection?.agent_actual_points,
      validationCode: values.userCode
    });

    if (repAgentInspection.status !== 1) {
      showWarningMessage({
        message: "Houve uma falha ao enviar o código do usuário."
      });
      setSubmitting(false);
      return;
    }

    setOpenInfoDialogue({
      open: true,
      msgId: INFO_DIALOGUE_MSGS.SUBMITTED_USER_CODE,
      callback: () => navigate(-1)
    });
    setSubmitting(false);
  }

  return (
    <div className="inner-page denounce-update-page">
      <Header
        showBackBtn
        hideBackBtnLabel
        pageTitle="Inspecionar Denúncia"
      />
      <div className="inner-page-content-container">
        <Stack spacing={2} className="inner-pages">
          <Pagination count={2} page={page} onChange={handleChange} className="pagination" />
          {page === 1 && (
            <div className="pagination-content-container">
              <Typography>Confirme o endereço</Typography>
              {
                isDoneLoading && denounce ? (
                  <AddressForm
                    onSubmit={handleSubmitAddressForm}
                    city={denounceAddress.city}
                    neighborhood={denounceAddress.neighborhood}
                    street={denounceAddress.street}
                    number={denounceAddress.number}
                    complement={denounceAddress.complement}
                    zipCode={denounceAddress.zipCode}
                  />
                ) : <></>
              }
            </div>
          )}
          {page === 2 && (
            <div className="pagination-content-container">
              <Typography>Atualize a denúncia</Typography>
              <CameraCapture
                initialImageUrl={vectorFocusClosureImageUrl}
                setSelectedFile={setVectorFocusImageFile}/>
              <UpdateDenounceForm
                denounce={denounce}
                denounceStatus={denounceStatus}
                onSubmit={handleSubmitDenounceForm} />
            </div>
          )}
          {page === 3 && (
            <div className="pagination-content-container">
              <Typography>Insira o código do usuário</Typography>
              <UserCodeForm
                onSubmit={handleSubmitUserCode}
                userCode={''} />
            </div>
          )}
        </Stack>
        <InformationDialogue
          title={infoMsgTitle}
          confirmLabel={infoMsgLabel}
          open={openInfoDialogue}
          onClose={handleCloseInfoDialogue}
        >
          {infoMsgContent}
        </InformationDialogue>
      </div>
      <BottomMenu />
    </div>
  );
}

export default DenounceUpdate;
